import { Box, Modal, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

interface LogoutConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onDeleteAccount?: () => void;
}

export function LogoutConfirmModal({
  open,
  onClose,
  onConfirm,
  onDeleteAccount,
}: LogoutConfirmModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const c = theme.palette.custom;

  return (
    <Modal
      open={open}
      onClose={() => {}}
      slotProps={{
        backdrop: { sx: { bgcolor: c.others.modalBackgroundBlack } },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "calc(100% - 2 * var(--Margin-Padding-M))",
          maxWidth: 480,
          bgcolor: "white",
          border: `1px solid ${c.gray[200]}`,
          borderRadius: "10px",
          boxShadow: "0 2px 40px rgba(0,0,0,0.1)",
          py: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            px: 3,
            py: 2,
            overflow: "hidden",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 32,
              lineHeight: 1.33,
              letterSpacing: "-0.32px",
              color: c.gray[900],
            }}
          >
            {t("auth.logout.title")}
          </Typography>
          <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: c.gray[700] }}>
            {t("auth.logout.description")}
          </Typography>
        </Box>

        <Box sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box
                component="button"
                onClick={onClose}
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 52,
                  px: 3,
                  py: 1,
                  borderRadius: "8px",
                  border: "none",
                  bgcolor: "transparent",
                  cursor: "pointer",
                  "&:hover": { bgcolor: c.primarySecondaryHover },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 16,
                    lineHeight: 1.5,
                    color: c.primary,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("auth.logout.cancel")}
                </Typography>
              </Box>
              <Box
                component="button"
                onClick={onConfirm}
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 52,
                  px: 3,
                  py: 1,
                  borderRadius: "8px",
                  border: `1px solid ${c.destructive}`,
                  bgcolor: "white",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: c.destructiveHover,
                    bgcolor: c.destructiveSecondaryHover,
                  },
                  "&:active": {
                    borderColor: c.destructivePressed,
                    bgcolor: c.destructiveSecondaryPressed,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 16,
                    lineHeight: 1.5,
                    color: c.destructive,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("auth.logout.confirm")}
                </Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: c.gray[600] }}>
              {t("auth.logout.deleteAccount")}{" "}
              <Box
                component="span"
                onClick={onDeleteAccount}
                sx={{
                  color: c.destructive,
                  textDecoration: "underline",
                  cursor: onDeleteAccount ? "pointer" : "default",
                }}
              >
                {t("auth.logout.clickHere")}
              </Box>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
